import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Brain, Clock, Trophy, BarChart2, CheckCircle } from "lucide-react";
import { useAuth } from "../../hooks/AuthenticationProvider";
import PvPLobby from "../../components/PvPLobby";
import MultiplayerGame from "../../components/MultiplayerGame";

const FocusFinderMultiWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLobby, setShowLobby] = useState(true);
  const [activeMatch, setActiveMatch] = useState(null);

  useEffect(() => {
    // Check if we have a match ID in URL
    const searchParams = new URLSearchParams(location.search);
    const matchId = searchParams.get("matchId");

    if (matchId) {
      setActiveMatch(matchId);
      setShowLobby(false);
    }
  }, [location]);

  const handleMatchCreated = (matchId) => {
    setActiveMatch(matchId);
    setShowLobby(false);
  };

  return (
    <div className="h-full">
      {showLobby ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <PvPLobby
            game="focus-finder" // IMPORTANT: Change this for each game!
            onMatchCreated={handleMatchCreated}
            onLeaveLobby={() => navigate("/gameroom")}
            gamesData={[{ id: "focus-finder", name: "Focus FInder" }]}
          />
        </div>
      ) : (
        <MultiplayerGame
          matchId={activeMatch}
          gameComponent={FocusFinderMulti} // This is your existing component
          onExit={() => navigate("/gameroom")}
        />
      )}
    </div>
  );
};

const FocusFinderMulti = ({
  gameState,
  updateGameState,
  submitScore,
  opponent,
}) => {
  const { user } = useAuth();

  // Game states
  const [gameMode, setGameMode] = useState("play"); // "play" | "results"
  const [gameStarted, setGameStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const timerRef = useRef(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [gameOverHandled, setGameOverHandled] = useState(false);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);

  // Countdown
  const [countdown, setCountdown] = useState(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const countdownRef = useRef(null);

  // Score and metrics
  const [score, setScore] = useState(0);
  const [hitCount, setHitCount] = useState(0);
  const [commissionErrors, setCommissionErrors] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  const [reactionTimes, setReactionTimes] = useState([]);

  // Stimuli sequence & current stimulus state
  const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
  const [stimuliSequence, setStimuliSequence] = useState([]);
  const [currentStimulus, setCurrentStimulus] = useState(null);
  const [currentResponseRecorded, setCurrentResponseRecorded] = useState(false);
  const [stimulusDisplayTime, setStimulusDisplayTime] = useState(null);

  // Game settings for multiplayer
  const GAME_DURATION = 60; // seconds
  const STIMULUS_INTERVAL = 2000; // 2 seconds per stimulus

  // Use a ref to always have the current value of isPlaying
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // New color scheme for the category:
  const primaryColor = "from-amber-500 to-orange-600";
  const primaryColorSolid = "bg-amber-500";

  // ----------------------------------
  //        MULTIPLAYER STATE
  // ----------------------------------
  useEffect(() => {
    if (gameState) {
      // If we're waiting for both players to be ready
      if (gameState.status === "pending") {
        setGameMode("play");
        setGameStarted(false);
        setWaitingForOpponent(false); // Reset waiting state
      }
      // If the game is getting ready to start (both players clicked ready)
      else if (gameState.status === "ready") {
        if (!gameStarted && !showCountdown) {
          // Both players are ready, start the countdown
          startGame();
        }
      }
      // If the game is active, make sure we're playing
      else if (gameState.status === "active") {
        if (!gameStarted) {
          setGameStarted(true);
          startGame();
        }
      }
      // If the game is completed, show results
      else if (gameState.status === "completed") {
        if (isPlaying) {
          endGame();
        }
      }

      // Check if opponent is ready (for UI indication)
      if (gameState.playerReady && opponent) {
        const opponentReady = gameState.playerReady[opponent.email] || false;
        console.log(`Opponent ready: ${opponentReady}`);
      }
    }
  }, [gameState, gameStarted, isPlaying, opponent, showCountdown]);

  useEffect(() => {
    if (gameState?.status === "completed" && !gameOverHandled) {
      endGame();
      setGameOverHandled(true);
    }
  }, [gameState?.status, gameOverHandled]);

  // Check if all players are ready
  useEffect(() => {
    if (!gameState || !opponent || !user?.email) return;

    // Check if all players are ready
    const playerReadyState = gameState.playerReady || {};
    const allPlayersReady =
      playerReadyState[user.email] && playerReadyState[opponent.email];

    // If all players are ready but game is still pending, transition to "ready" state
    if (allPlayersReady && gameState.status === "pending" && updateGameState) {
      updateGameState({
        status: "ready",
      });
    }
  }, [
    gameState?.playerReady,
    opponent,
    user?.email,
    updateGameState,
    gameState?.status,
  ]);

  // --- Generate Stimuli Sequence ---
  const generateStimuliSequence = useCallback(() => {
    const numStimuli = Math.floor((GAME_DURATION * 1000) / STIMULUS_INTERVAL);

    const sequence = [];
    // In multiplayer, use standard difficulty (level 2)
    const targetProbability = 0.15;

    for (let i = 0; i < numStimuli; i++) {
      const isTarget = Math.random() < targetProbability;
      let letter;
      if (isTarget) {
        letter = "X"; // Target letter
      } else {
        // Choose a random letter from A-Z (excluding "X" for clarity)
        const letters = "ABCDEFGHIKLMNOPQRSTUVWYZ";
        letter = letters[Math.floor(Math.random() * letters.length)];
      }
      sequence.push({ letter, isTarget });
    }

    return sequence;
  }, []);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // --- Start Game with countdown ---
  const startGame = () => {
    setShowCountdown(true);
    setCountdown(3);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          startGameAfterCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start the actual game after countdown
  const startGameAfterCountdown = () => {
    setShowCountdown(false);

    // Reset all game states
    setScore(0);
    setReactionTimes([]);
    setHitCount(0);
    setCommissionErrors(0);
    setMissedTargets(0);
    setCurrentStimulusIndex(0);
    setTimeLeft(GAME_DURATION);

    // Start the timer countdown
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // IMPORTANT: Make these separate state updates
    setIsPlaying(true);
    setGameStarted(true);

    const sequence = generateStimuliSequence();
    setStimuliSequence(sequence);

    // Start first round with a guaranteed delay
    setTimeout(() => {
      console.log("Starting first round after countdown");
      showNextStimulus(0, sequence);
    }, 500);

    // Update the game state
    if (updateGameState) {
      updateGameState({
        status: "active",
        playerReady: true,
        myProgress: 0,
        myScore: 0,
      });
    }
  };

  // --- Show Next Stimulus ---
  const showNextStimulus = (index, sequence) => {
    if (index >= sequence.length) {
      endGame();
      return;
    }

    // If the previous stimulus was a target and no response was made, count it as a miss
    if (
      currentStimulus &&
      currentStimulus.isTarget &&
      !currentResponseRecorded
    ) {
      setMissedTargets((prev) => prev + 1);
    }

    const newStimulus = sequence[index];

    setCurrentStimulus(newStimulus);
    setCurrentStimulusIndex(index);
    setCurrentResponseRecorded(false);
    setStimulusDisplayTime(Date.now());

    // Schedule the next stimulus using our isPlayingRef to get the latest value
    setTimeout(() => {
      if (isPlayingRef.current) {
        showNextStimulus(index + 1, sequence);
      }
    }, STIMULUS_INTERVAL);
  };

  // --- Handle User Response ---
  const handleUserResponse = () => {
    if (!isPlayingRef.current || currentResponseRecorded) {
      return;
    }

    const responseTime = Date.now() - stimulusDisplayTime;

    setCurrentResponseRecorded(true);

    if (currentStimulus && currentStimulus.isTarget) {
      setHitCount((prev) => prev + 1);

      // Score weighting: base + small speed bonus
      let basePoints = 10; // each correct
      // Speed bonus: faster than 1 sec => +5, <2 sec => +3, <3 => +2, else +1
      let speedBonus = 1;
      if (responseTime < 1000) {
        speedBonus = 5;
      } else if (responseTime < 2000) {
        speedBonus = 3;
      } else if (responseTime < 3000) {
        speedBonus = 2;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);
      setReactionTimes((prev) => [...prev, responseTime]);

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentStimulusIndex / stimuliSequence.length,
          myScore: score + roundScore,
          myLastResponse: {
            correct: true,
            points: roundScore,
            responseTime,
          },
        });
      }
    } else {
      setCommissionErrors((prev) => prev + 1);
      // Small penalty
      setScore((prev) => (prev > 0 ? prev - 5 : 0));

      // Update the game state
      if (updateGameState) {
        updateGameState({
          myProgress: currentStimulusIndex / stimuliSequence.length,
          myScore: score > 0 ? score - 5 : 0,
          myLastResponse: {
            correct: false,
            points: -5,
            responseTime,
          },
        });
      }
    }
  };

  // --- Calculate Reaction Time Variability ---
  const calculateRTVariability = () => {
    if (reactionTimes.length < 2) return null;
    const mean =
      reactionTimes.reduce((acc, rt) => acc + rt, 0) / reactionTimes.length;
    const variance =
      reactionTimes.reduce((acc, rt) => acc + Math.pow(rt - mean, 2), 0) /
      (reactionTimes.length - 1);
    return Math.round(Math.sqrt(variance));
  };

  // --- End Game ---
  const endGame = () => {
    if (scoreSubmitted) return;
    setScoreSubmitted(true);

    setIsPlaying(false);
    setGameMode("results");
    clearInterval(timerRef.current);

    // Submit the score + detailed stats
    if (submitScore) {
      // Create a stats object with all the performance metrics
      const stats = {
        hitCount,
        commissionErrors,
        missedTargets,
        averageReactionTime: Math.round(
          reactionTimes.length > 0
            ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
            : 0
        ),
        reactionTimeVariability: calculateRTVariability(),
        totalGames: hitCount + commissionErrors + missedTargets,
        gameLength: GAME_DURATION - timeLeft,
        timestamp: new Date(),
      };

      // Submit both score and stats
      submitScore(score, stats);

      // Also update the game state one last time with final score and progress
      if (updateGameState) {
        updateGameState({
          myProgress: 1, // 100% progress
          myScore: score,
          myLastResponse: {
            gameComplete: true,
            finalStats: stats,
          },
        });
      }
    }
  };

  // Check if the opponent has quit
  useEffect(() => {
    if (gameState?.playerQuit && gameState.playerQuit !== user?.email) {
      // If we're still playing, end the game automatically with a win
      if (isPlaying) {
        endGame();
        // You could optionally show a special message:
        alert("Your opponent has left the game. You win by forfeit!");
      }
    }
  }, [gameState?.playerQuit, user?.email, isPlaying]);

  // Handler for the Ready to Play button
  const handleReadyClick = () => {
    console.log("Ready to Play clicked");
    setWaitingForOpponent(true);

    // Update the game state to indicate this player is ready
    if (updateGameState) {
      updateGameState({
        playerReady: {
          ...(gameState?.playerReady || {}),
          [user.email]: true,
        },
        // Add player device/connection info
        playerInfo: {
          ...(gameState?.playerInfo || {}),
          [user.email]: {
            device: navigator.userAgent,
            connectionTime: new Date().toISOString(),
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
          },
        },
      });
    }
  };

  return (
    <div className="w-full text-white p-0">
      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-amber-300 font-medium flex items-center gap-2">
            <Brain size={18} className="text-amber-300" />
            <span>Attention Guardian</span>
          </div>
        </div>

        {/* Game Container */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          {/* Play Screen */}
          <div className="relative p-6 md:p-8">
            {/* Start button or waiting indicator */}
            {!gameStarted && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                {waitingForOpponent ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-t-transparent border-amber-500 rounded-full animate-spin"></div>
                    <p className="text-white text-xl">
                      Waiting for opponent...
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={handleReadyClick}
                    className="py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 hover:brightness-110 text-white rounded-lg"
                  >
                    Ready to Play
                  </button>
                )}
              </div>
            )}

            {/* Countdown overlay */}
            {showCountdown && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/80">
                <div className="flex flex-col items-center">
                  <div className="text-6xl font-bold text-white mb-4">
                    {countdown > 0 ? countdown : "GO!"}
                  </div>
                  <div className="text-xl text-slate-300">Get ready...</div>
                </div>
              </div>
            )}

            {/* Game stats header */}
            <div className="flex justify-between items-center mb-6">
              <div className="bg-gradient-to-r from-amber-500/20 to-orange-600/20 px-4 py-2 rounded-lg">
                <span className="text-amber-300 font-medium capitalize">
                  Multiplayer Match
                </span>
              </div>

              <div className="flex items-center">
                <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                  <Clock size={18} className="text-slate-400" />
                  <span className="text-white font-mono">
                    {formatTime(timeLeft)}
                  </span>
                </div>

                <div className="ml-4 bg-amber-500/20 px-4 py-2 rounded-lg">
                  <span className="text-white font-medium">Score: {score}</span>
                </div>
              </div>
            </div>

            {/* Opponent progress */}
            {gameState && opponent && (
              <div className="mb-4 bg-slate-800/60 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-slate-300">
                    {opponent.name}'s Progress
                  </span>
                  <span className="text-xs text-white">
                    {gameState?.progress && gameState.progress[opponent.email]
                      ? `${Math.round(
                          gameState.progress[opponent.email] * 100
                        )}%`
                      : "0%"}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="bg-amber-500 h-full rounded-full"
                    style={{
                      width: `${
                        ((gameState?.progress &&
                          gameState.progress[opponent.email]) ||
                          0) * 100
                      }%`,
                    }}
                  ></div>
                </div>
                {gameState?.scores &&
                  gameState.scores[opponent.email] !== undefined && (
                    <div className="mt-1 text-right text-xs text-amber-300">
                      Score: {gameState.scores[opponent.email]}
                    </div>
                  )}
              </div>
            )}

            {/* Main Game Content */}
            <div className="flex flex-col items-center justify-center">
              {/* Stimulus Display */}
              <div className="flex flex-col items-center mb-8">
                <h3 className="text-white font-medium mb-2">
                  Press the button when you see the letter "X"
                </h3>
                <div
                  className={`w-32 h-32 md:w-40 md:h-40 ${primaryColorSolid} rounded-full 
                              flex items-center justify-center text-5xl font-bold text-white mb-8`}
                >
                  {currentStimulus ? currentStimulus.letter : ""}
                </div>

                {/* Target Button */}
                <button
                  onClick={handleUserResponse}
                  className="w-64 py-4 bg-green-500/20 hover:bg-green-500/30 text-green-400 
                            font-medium rounded-lg transition-colors flex items-center 
                            justify-center gap-2 text-lg"
                  disabled={!isPlaying}
                >
                  <CheckCircle size={24} />
                  Target
                </button>
              </div>

              {/* Game Stats */}
              <div className="mt-6 max-w-lg mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Hits</div>
                  <div className="text-green-400 font-medium">{hitCount}</div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {commissionErrors}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Misses</div>
                  <div className="text-amber-400 font-medium">
                    {missedTargets}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Avg. Time</div>
                  <div className="text-white font-medium">
                    {reactionTimes.length > 0
                      ? `${Math.round(
                          reactionTimes.reduce((a, b) => a + b, 0) /
                            reactionTimes.length
                        )} ms`
                      : "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mx-auto flex items-center justify-center mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You scored {score} points in your multiplayer match!
                </p>
              </div>

              {/* Match results comparison */}
              {gameState && gameState.status === "completed" && opponent && (
                <div className="bg-slate-800/70 border border-slate-700 rounded-xl p-4 mb-6">
                  <h3 className="text-lg font-bold text-white text-center mb-4">
                    Match Results
                  </h3>

                  <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {user?.firstName || user?.email?.split("@")[0]}
                      </div>
                      <div className="text-3xl font-bold text-amber-300 mt-2">
                        {score}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>

                    <div className="flex flex-col items-center px-4">
                      <div className="text-xl font-bold text-white">VS</div>
                      <div className="w-px h-16 bg-slate-600 my-2"></div>
                    </div>

                    <div className="text-center flex-1 p-3">
                      <div className="text-lg font-medium text-white">
                        {opponent.name}
                      </div>
                      <div className="text-3xl font-bold text-amber-300 mt-2">
                        {gameState.scores?.[opponent.email] || 0}
                      </div>
                      <div className="text-sm text-slate-400 mt-1">points</div>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    {score > (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 py-2 px-4 rounded-lg font-medium">
                        You Won! 🏆
                      </div>
                    ) : score === (gameState.scores?.[opponent.email] || 0) ? (
                      <div className="bg-amber-500/20 border border-amber-500/30 text-amber-300 py-2 px-4 rounded-lg font-medium">
                        It's a Tie! 🤝
                      </div>
                    ) : (
                      <div className="bg-blue-500/20 border border-blue-500/30 text-blue-300 py-2 px-4 rounded-lg font-medium">
                        {opponent.name} Won! Better luck next time!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Performance stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-amber-300" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-amber-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Hits</span>
                      <span className="text-green-400">{hitCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Commission Errors</span>
                      <span className="text-red-400">{commissionErrors}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Missed Targets</span>
                      <span className="text-amber-400">{missedTargets}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {reactionTimes.length > 0
                          ? `${Math.round(
                              reactionTimes.reduce((a, b) => a + b, 0) /
                                reactionTimes.length
                            )} ms`
                          : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">RT Variability</span>
                      <span className="text-white">
                        {calculateRTVariability()
                          ? `${calculateRTVariability()} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-amber-300" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {hitCount + commissionErrors > 0
                            ? `${Math.round(
                                (hitCount / (hitCount + commissionErrors)) * 100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-orange-600"
                          style={{
                            width:
                              hitCount + commissionErrors > 0
                                ? `${
                                    (hitCount / (hitCount + commissionErrors)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="space-y-3 mt-4">
                      <h4 className="text-white font-medium mb-2">
                        Key Cognitive Skills
                      </h4>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-amber-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-amber-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Sustained attention and vigilance
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-amber-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-amber-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Response inhibition
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-amber-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-amber-400" />
                          </div>
                          <span className="text-slate-300 text-sm">
                            Cognitive endurance and focus
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return to lobby button */}
              <div className="flex justify-center">
                <button
                  onClick={() => submitScore(score)}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Return to Lobby
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FocusFinderMultiWrapper;
